import { Menu, MenuItem, MenuItemButton, usePopoverState } from '@nucleus/react-components';
import { ShareButton } from '@nucleus/sermon-theme-elements';
import { PostHeaderSectionWeb } from '@nucleus/types/web/sections/post-header';
import { useSection } from '@nucleus/web-theme';
import { buildTransparentColorFromCssVariable, nucleusClass } from '@nucleus/web-theme-elements';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import React from 'react';
import styled, { css } from 'styled-components';
import { InfoPosition, InfoSizePartition } from '../../blocks/Base';
import { Body, Byline, Headline, Overline } from '../../components/Base';
import { getSectionClasses } from '../../lib/classes';
import { getWindow } from '../../lib/dom';
import { Section } from '../../sectionLayouts/Section';
import { SectionLayout } from '../../sectionLayouts/SectionLayout';
import { PostContentBlockLayout } from '../PostContent/PostContentBlockLayout';

export const PostHeaderSection = (): JSX.Element => {
  const section = useSection<PostHeaderSectionWeb>();

  const isLayout6 = section.payload.blockLayout === 'layout6';
  const isLayoutFull = section.payload.layout === 'full' || section.payload.layout === undefined;

  const layoutProps = { ...section.payload };
  if (isLayoutFull === true && isLayout6 !== true) {
    delete layoutProps.backgroundMedia;
  }

  const blockProps = { ...section.payload };
  if (isLayoutFull !== true) {
    delete blockProps.backgroundMedia;
  }

  return (
    <PositionedSection
      className={classNames(nucleusClass('section-post-header'), getSectionClasses(section))}
      isLayout4={section.payload.blockLayout === 'layout4'}
    >
      <StyledSectionLayout {...layoutProps}>
        <PostContentBlockLayout {...blockProps} />
      </StyledSectionLayout>
      <ShareButtonContainer>
        <PostHeaderShareMenu />
      </ShareButtonContainer>
    </PositionedSection>
  );
};

const PostHeaderShareMenu = (): JSX.Element => {
  const { popoverProps, triggerProps } = usePopoverState();
  const shareUrl = getWindow()?.location.href ?? '';

  return (
    <>
      <StyledShareButton {...triggerProps} />
      <Menu {...popoverProps}>
        <CopyLinkItem url={shareUrl}>Copy Link</CopyLinkItem>
        <ShareItem title={getWindow()?.document?.title ?? ''} url={shareUrl}>
          Sharing Options
        </ShareItem>
      </Menu>
    </>
  );
};

const StyledShareButton = styled(ShareButton)`
  background: ${buildTransparentColorFromCssVariable('--color-section-text', 0.15)};
  backdrop-filter: blur(21px);
`;

const CopyLinkItem = (props: { children?: React.ReactNode; url: string }): JSX.Element => {
  const [isCopied, setIsCopied] = React.useState(false);

  const handleCopyLink = () => {
    copy(props.url);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  return (
    <MenuItem>
      <MenuItemButton onClick={handleCopyLink}>{isCopied === true ? 'Copied!' : props.children}</MenuItemButton>
    </MenuItem>
  );
};

const ShareItem = (props: { children?: React.ReactNode; title: string; url: string }): JSX.Element | null => {
  const handleShareSheet = () => {
    if (navigator.share) {
      void navigator.share({
        text: props.title,
        url: props.url,
      });
    }
  };

  if (typeof navigator === 'undefined' || navigator.share === undefined) {
    return null;
  }

  return (
    <MenuItem>
      <MenuItemButton onClick={handleShareSheet}>{props.children}</MenuItemButton>
    </MenuItem>
  );
};

const PositionedSection = styled(Section)<{ isLayout4: boolean }>`
  position: relative;

  ${(props) =>
    props.isLayout4 &&
    css`
      ${InfoPosition} > div {
        max-width: 1080px;
        margin-left: auto;
        margin-right: auto;
      }
    `}
`;

const ShareButtonContainer = styled.div`
  position: absolute;
  bottom: calc(var(--unit-length) * 1);
  right: calc(var(--unit-length) * 1);
`;

const clampFontFactory = (min: string, cssVariable: string, max: string) =>
  `clamp(${min}, var(${cssVariable}) * var(--global-font-factor), ${max})`;

const StyledSectionLayout = styled(SectionLayout)`
  ${InfoPosition} ${InfoSizePartition} {
    ${Overline} {
      opacity: 0.75;
      margin: 0;
      margin-bottom: 1.8em;

      @media (max-width: 600px) {
        margin-top: 1.8em;
      }
    }

    ${Headline} > * {
      margin: 0;
      margin-bottom: 0.28em;
      font-size: min(var(--headline1-font-size), 9.6em);
    }

    ${Byline} > * {
      font-size: ${clampFontFactory('20px', '--label1-font-size', '24px')};
      margin: 0;
      margin-bottom: 0.55em;
      max-width: 70%;
    }

    ${Body}, ${Body} > * {
      opacity: 0.75;
      margin: 0;
    }
  }
`;
